import { useCallback, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useAppSelector } from 'src/app/store/hooks';
import { RootState } from 'src/app/store/types';
import {
  useLocationsGeolocationsCreateMutation,
  useLocationsGeolocationsDestroyMutation,
  useLocationsGeolocationsPartialUpdateMutation,
  useLocationsGeolocationsUndeleteCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { generateGeolocationToast } from './generateToast';
import {
  ArchiveGeolocationCompletion,
  CreateUpdateGeolocationCompletion,
  GeolocationChangeType,
  GeolocationFormValues,
} from './types';

interface GeolocationActionHandlers {
  onError: (error: string) => void;
  onCreateUpdateComplete: CreateUpdateGeolocationCompletion;
  onArchiveComplete: ArchiveGeolocationCompletion;
  onUnarchiveComplete: ArchiveGeolocationCompletion;
}

export const useGeolocationActions = ({
  onError,
  onCreateUpdateComplete,
  onArchiveComplete,
  onUnarchiveComplete,
}: GeolocationActionHandlers) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { displaySuccess } = useSuccessToast();
  const onCreateUpdateSuccess: CreateUpdateGeolocationCompletion = useCallback(
    (result, changeType) => {
      onCreateUpdateComplete(result, changeType);
      displaySuccess(changeType);
    },
    [onCreateUpdateComplete, displaySuccess]
  );

  const { createUpdateGeolocation } = useCreateUpdateGeolocation(
    onCreateUpdateSuccess
  );

  const onArchiveSuccess: ArchiveGeolocationCompletion = useCallback(
    (id) => {
      onArchiveComplete(id);
      displaySuccess('archive');
    },
    [onArchiveComplete, displaySuccess]
  );

  const { destroyGeolocation } = useDestroyGeolocation(onArchiveSuccess);

  const onUnarchiveSuccess: ArchiveGeolocationCompletion = useCallback(
    (id) => {
      onUnarchiveComplete(id);
      displaySuccess('unarchive');
    },
    [onUnarchiveComplete, displaySuccess]
  );

  const { undeleteGeolocation } = useUndeleteGeolocation(onUnarchiveSuccess);

  const submitGeolocation = useCallback(
    async (values: GeolocationFormValues) => {
      setIsSubmitting(true);
      try {
        await createUpdateGeolocation(values);
      } catch (error) {
        let msg = '';
        if (error instanceof GeolocationFormError) {
          msg = error.message;
        } else {
          msg = formatServerError(error);
        }
        onError(msg);
      }
      setIsSubmitting(false);
    },
    [onError, createUpdateGeolocation]
  );

  const archiveGeolocation = useCallback(
    async (id: string) => {
      try {
        await destroyGeolocation(id);
      } catch (error) {
        const msg = formatServerError(error);
        onError(msg);
      }
    },
    [destroyGeolocation, onError]
  );

  const unarchiveGeolocation = useCallback(
    async (id: string, geoLocation: any) => {
      try {
        await undeleteGeolocation(id, geoLocation);
      } catch (error) {
        const msg = formatServerError(error);
        onError(msg);
      }
    },
    [undeleteGeolocation, onError]
  );

  return {
    submitGeolocation,
    archiveGeolocation,
    unarchiveGeolocation,
    isSubmitting,
  };
};

const useCreateUpdateGeolocation = (
  onSuccess: CreateUpdateGeolocationCompletion
) => {
  const [createGeolocation] = useLocationsGeolocationsCreateMutation();
  const [updateGeolocation] = useLocationsGeolocationsPartialUpdateMutation();

  const createUpdateGeolocation = useCallback(
    async (values: GeolocationFormValues) => {
      if (!values.coordinates) {
        throw new GeolocationFormError('Please select a location on the map');
      }
      if (values.id) {
        const result = await updateGeolocation({
          id: values.id,
          patchedGeoLocation: {
            label: values.label,
          },
        }).unwrap();
        onSuccess(result, 'update');
      } else {
        const result = await createGeolocation({
          geoLocation: {
            label: values.label,
            location: values.coordinates,
          },
        }).unwrap();
        onSuccess(result, 'create');
      }
    },
    [createGeolocation, onSuccess, updateGeolocation]
  );

  return { createUpdateGeolocation };
};

const useDestroyGeolocation = (onSuccess: ArchiveGeolocationCompletion) => {
  const [destroyGeolocationMutation] =
    useLocationsGeolocationsDestroyMutation();

  const destroyGeolocation = useCallback(
    async (id: string) => {
      await destroyGeolocationMutation({ id }).unwrap();
      onSuccess(id);
    },
    [destroyGeolocationMutation, onSuccess]
  );

  return { destroyGeolocation };
};

const useUndeleteGeolocation = (onSuccess: ArchiveGeolocationCompletion) => {
  const [undeleteGeolocationMutation] =
    useLocationsGeolocationsUndeleteCreateMutation();

  const undeleteGeolocation = useCallback(
    async (id: string, geoLocation: any) => {
      await undeleteGeolocationMutation({
        id,
        geoLocation,
      }).unwrap();
      onSuccess(id);
    },
    [undeleteGeolocationMutation, onSuccess]
  );

  return { undeleteGeolocation };
};

const useSuccessToast = () => {
  const dispatch = useDispatch();
  const nextToastId = useAppSelector((state: RootState) => state.toast.nextId);

  const displaySuccess = useCallback(
    (changeType: GeolocationChangeType) => {
      const toast = generateGeolocationToast(nextToastId, changeType);
      dispatch(show(toast));
    },
    [dispatch, nextToastId]
  );

  return { displaySuccess };
};

class GeolocationFormError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeolocationFormError';
  }
}
